import { useLocale as useLocaleR42 } from '@/lib/locale';
import { CoachChip } from '@/components/CoachChip';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fmtPrice, fmtTime12h, currentSchoolCurrency } from '@/lib/api';
import { formatDateRange, formatSlotSchedule } from '@/lib/packageSchedule';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DateOfBirthInput from '@/components/DateOfBirthInput';
import { toast } from 'sonner';
import {
    ShoppingCart, Trash2, Plus, ArrowRight, Repeat, CalendarRange,
    Clock, Check, X, Sparkles, MapPin, Tent, BellRing,
} from 'lucide-react';

/**
 * ClassFinderWizard — Phase 103b
 * -------------------------------
 * Age-first guided class chooser rendered on the coach's PUBLIC page when
 * the coach has Programs configured. Replaces the flat package grid.
 *
 * Steps (progressive disclosure, mock-approved):
 *   1. Swimmer age (range chips)
 *   2. Program (cards filtered by age — non-fits dim out)
 *   3. Dates & time (3 variants: fixed sessions / ongoing monthly / flexible)
 *   4. Who's swimming (multi-kid chips + inline add)
 *   5. One checkout (sticky cart → /coach/{slug}/enroll-cart)
 *
 * Cart supports MIXED items (one-time + monthly memberships across multiple
 * kids) — a single card entry covers everything at checkout.
 */

export const AGE_RANGES = [
    { key: 'under3', label: 'Under 3', min: 0, max: 2 },
    { key: '3-5', label: '3–5', min: 3, max: 5 },
    { key: '6-9', label: '6–9', min: 6, max: 9 },
    { key: '10plus', label: '10+', min: 10, max: 17 },
    { key: 'adult', label: 'Adult', min: 18, max: 120 },
];

const WD_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function slotChipLabel(slot) {
    if (!slot) return '';
    const time = slot.time_local ? fmtTime12h(slot.time_local) : '';
    if ((slot.type || 'weekly') === 'one_off' && slot.date) {
        return `${slot.date.slice(5)} · ${time}`;
    }
    const wds = Array.isArray(slot.weekdays) && slot.weekdays.length
        ? slot.weekdays
        : (slot.weekday !== undefined && slot.weekday !== null ? [slot.weekday] : []);
    const names = wds.map((d) => WD_SHORT[((Number(d) % 7) + 7) % 7]).join('/');
    return [names, time].filter(Boolean).join(' ');
}

function programFitsRange(prog, range) {
    if (!range) return true;
    const pMin = prog.age_min ?? 0;
    const pMax = (prog.age_max === null || prog.age_max === undefined) ? 120 : prog.age_max;
    return pMin <= range.max && pMax >= range.min;
}

function ageFromDob(dob) {
    if (!dob) return null;
    const d = new Date(dob + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
    return Math.max(0, age);
}

// Mirror of the backend proration estimator — display-only. Stripe computes
// the authoritative amount per-second at subscription creation.
export function estimateProration(priceCents, billingDay) {
    const now = new Date();
    const target = Math.max(1, Math.min(28, Number(billingDay) || 1));
    let year = now.getUTCFullYear();
    let month = now.getUTCMonth(); // 0-based
    if (now.getUTCDate() >= target) {
        month += 1;
        if (month > 11) { month = 0; year += 1; }
    }
    const anchor = new Date(Date.UTC(year, month, target, 12, 0, 0));
    const prevMonth = month === 0 ? 11 : month - 1;
    const prevYear = month === 0 ? year - 1 : year;
    const periodStart = new Date(Date.UTC(prevYear, prevMonth, target, 12, 0, 0));
    const total = anchor.getTime() - periodStart.getTime();
    const remaining = Math.max(0, anchor.getTime() - now.getTime());
    const frac = total > 0 ? Math.min(1, remaining / total) : 0;
    return { cents: Math.round((priceCents || 0) * frac), anchorIso: anchor.toISOString().slice(0, 10) };
}

const cartStorageKey = (slug) => `sp_cart_${slug}`;

export function loadStoredCart(slug) {
    try {
        const raw = sessionStorage.getItem(cartStorageKey(slug));
        return raw ? JSON.parse(raw) : null;
    } catch { return null; }
}

export function storeCart(slug, cart) {
    try { sessionStorage.setItem(cartStorageKey(slug), JSON.stringify(cart)); } catch { /* ignore */ }
}

export function clearStoredCart(slug) {
    try { sessionStorage.removeItem(cartStorageKey(slug)); } catch { /* ignore */ }
}

const newUid = () => (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const ClassFinderWizard = ({ slug, coach, packages, programs, locations = [], primary, previewMode, embedded, onRequestTime }) => {
    const { t: t42 } = useLocaleR42();
    const navigate = useNavigate();
    const accent = primary || '#00d4ff';

    const [ageKey, setAgeKey] = useState(null);
    const [programId, setProgramId] = useState(null);
    const [packageId, setPackageId] = useState(null);
    const [slotIds, setSlotIds] = useState([]);
    const [singleLessonAt, setSingleLessonAt] = useState('');
    const [slotCapacity, setSlotCapacity] = useState({});
    // Swimmers — shared across cart items so siblings reuse one entry.
    const [swimmers, setSwimmers] = useState([]);
    const [selectedSwimmerUids, setSelectedSwimmerUids] = useState([]);
    const [addingSwimmer, setAddingSwimmer] = useState(false);
    const [newSwimmer, setNewSwimmer] = useState({ first_name: '', dob: '' });
    const [items, setItems] = useState([]);
    const stepScheduleRef = useRef(null);

    // Phase 164 — waitlist: slot id → my waiting entry with this coach. Full
    // chips on a class the coach opted in (`waitlist_enabled`) become
    // "Join waitlist" / "On waitlist" toggles instead of dead buttons.
    const [waitlist, setWaitlist] = useState({});
    const [wlBusy, setWlBusy] = useState(null);
    const loadWaitlist = async () => {
        if (!slug || previewMode) return;
        try {
            const r = await api.get('/parent/waitlist', { params: { slug } });
            const map = {};
            for (const e of r.data?.entries || []) map[e.class_slot_id] = e;
            setWaitlist(map);
        } catch { /* parents who aren't logged in yet simply see plain Full chips */ }
    };
    useEffect(() => { loadWaitlist(); }, [slug, previewMode]); // eslint-disable-line react-hooks/exhaustive-deps
    const toggleWaitlist = async (pkg, slot) => {
        if (previewMode) { toast.info(t42("Preview only — approved parents join the waitlist here.")); return; }
        setWlBusy(slot.id);
        try {
            const entry = waitlist[slot.id];
            if (entry) {
                await api.delete(`/parent/waitlist/${entry.id}`);
                toast.success(t42("Removed from the waitlist."));
            } else {
                const r = await api.post('/parent/waitlist', { slug, package_id: pkg.id, class_slot_id: slot.id });
                toast.success(r.data?.already ? "You're already on this waitlist." : "You're on the waitlist!", {
                    description: 'Your coach will let you know when a spot opens — first family to book gets it.',
                });
            }
            await loadWaitlist();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't update the waitlist. Try again.");
        } finally { setWlBusy(null); }
    };

    // Restore a previously-built cart (parent came back from checkout).
    useEffect(() => {
        const stored = loadStoredCart(slug);
        if (stored?.items?.length) {
            setItems(stored.items);
            setSwimmers(stored.swimmers || []);
        }
    }, [slug]);

    // Persist cart as it changes so checkout + back-navigation stay in sync.
    useEffect(() => {
        storeCart(slug, { items, swimmers });
    }, [slug, items, swimmers]);

    const range = AGE_RANGES.find((r) => r.key === ageKey) || null;
    const fitCount = useMemo(
        () => programs.filter((p) => programFitsRange(p, range)).length,
        [programs, range],
    );
    const selectedProgram = programs.find((p) => p.id === programId) || null;

    // Phase 107 — location filter. Only surfaces when the classes in play
    // actually span 2+ places (multiple fixed locations, or a mix of fixed +
    // "coach travels to you" mobile packages). 'null' = all locations.
    const [locationFilter, setLocationFilter] = useState(null);
    const locationsById = useMemo(
        () => Object.fromEntries((locations || []).map((l) => [l.id, l])),
        [locations],
    );
    const locationChoices = useMemo(() => {
        const ids = [];
        let hasMobile = false;
        for (const p of packages) {
            if (!p.program_id) continue; // only classes reachable via the wizard
            if (p.delivery_mode === 'mobile') { hasMobile = true; continue; }
            if (p.location_id && locationsById[p.location_id] && !ids.includes(p.location_id)) ids.push(p.location_id);
        }
        const opts = ids
            .map((id) => locationsById[id])
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        return { opts, hasMobile };
    }, [packages, locationsById]);
    const showLocationFilter = (locationChoices.opts.length + (locationChoices.hasMobile ? 1 : 0)) > 1;
    const matchesLocation = (p) => {
        if (!locationFilter) return true;
        if (locationFilter === 'mobile') return p.delivery_mode === 'mobile';
        return p.location_id === locationFilter;
    };
    const pickLocationFilter = (value) => {
        setLocationFilter(value);
        // Deselect a class that just got filtered out so the parent can't
        // carry a hidden selection through checkout.
        if (value && packageId) {
            const cur = packages.find((p) => p.id === packageId);
            const stillVisible = cur && (value === 'mobile' ? cur.delivery_mode === 'mobile' : cur.location_id === value);
            if (!stillVisible) { setPackageId(null); setSlotIds([]); }
        }
    };
    const locationLabelFor = (p) => {
        if (p.delivery_mode === 'mobile') return 'Coach travels to you';
        const loc = p.location_id ? locationsById[p.location_id] : null;
        return loc?.name || '';
    };

    const programPackages = useMemo(
        () => packages.filter((p) => p.program_id === programId),
        [packages, programId],
    );
    const visibleProgramPackages = useMemo(
        () => programPackages.filter((p) => matchesLocation(p)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [programPackages, locationFilter],
    );
    const selectedPkg = programPackages.find((p) => p.id === packageId) || null;
    const pkgSlots = selectedPkg?.class_slots || [];
    const requiredSlots = selectedPkg
        ? (selectedPkg.billing_type === 'recurring_monthly'
            ? Math.max(1, Math.min(pkgSlots.length || 1, Number(selectedPkg.sessions_per_week) || 1))
            : (pkgSlots.length ? 1 : 0))
        : 0;
    const isSingleLessonNoSlots = selectedPkg
        && !pkgSlots.length
        && (selectedPkg.lesson_type === 'single_lesson' || (Number(selectedPkg.session_count) === 1 && !selectedPkg.flexible_schedule && selectedPkg.billing_type === 'one_time'));

    // Live seats-left for the selected package.
    useEffect(() => {
        if (!packageId) { setSlotCapacity({}); return undefined; }
        let cancelled = false;
        api.get(`/public/coach/${slug}/package/${packageId}/slot-capacity`)
            .then((r) => {
                if (cancelled) return;
                const map = {};
                for (const row of (r.data?.slots || [])) {
                    map[row.slot_id] = { capacity: row.capacity || 0, taken: row.taken || 0 };
                }
                setSlotCapacity(map);
            })
            .catch(() => { /* capacity chips are best-effort */ });
        return () => { cancelled = true; };
    }, [slug, packageId]);

    const pickAge = (key) => {
        setAgeKey(key);
        setProgramId(null);
        setPackageId(null);
        setSlotIds([]);
    };

    const pickProgram = (p) => {
        setProgramId(p.id);
        setPackageId(null);
        setSlotIds([]);
        setTimeout(() => stepScheduleRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    };

    const pickPackage = (p) => {
        setPackageId(p.id);
        setSlotIds([]);
        setSingleLessonAt('');
    };

    const toggleSlot = (slot) => {
        const cap = slotCapacity[slot.id];
        const seatsLeft = cap ? Math.max(0, cap.capacity - cap.taken) : null;
        if (cap && cap.capacity > 0 && seatsLeft <= 0) {
            toast.error(t42("That time is full — pick another."));
            return;
        }
        if (cap?.conflict_requires_request) {
            // Phase 171 — can't be bought instantly; the coach confirms a request first.
            toast.info(t42("Your coach needs to confirm this time — use \"Request this time\" instead."));
            return;
        }
        setSlotIds((prev) => {
            if (prev.includes(slot.id)) return prev.filter((s) => s !== slot.id);
            if (requiredSlots === 1) return [slot.id];
            if (prev.length >= requiredSlots) {
                toast.info(`Pick up to ${requiredSlots} days — remove one first.`);
                return prev;
            }
            return [...prev, slot.id];
        });
    };

    const toggleSwimmer = (uid) => {
        setSelectedSwimmerUids((prev) => prev.includes(uid) ? prev.filter((u) => u !== uid) : [...prev, uid]);
    };

    const addSwimmer = () => {
        const fn = (newSwimmer.first_name || '').trim();
        if (!fn) { toast.error(t42("Enter the swimmer's first name.")); return; }
        if (!newSwimmer.dob) { toast.error(t42("Enter the swimmer's birthdate so we can match ages.")); return; }
        const s = { uid: newUid(), first_name: fn, dob: newSwimmer.dob };
        setSwimmers((prev) => [...prev, s]);
        setSelectedSwimmerUids((prev) => [...prev, s.uid]);
        setNewSwimmer({ first_name: '', dob: '' });
        setAddingSwimmer(false);
    };

    const scheduleReady = selectedPkg && (
        (requiredSlots === 0 && (!isSingleLessonNoSlots || !!singleLessonAt))
        || (requiredSlots > 0 && slotIds.length === requiredSlots)
    );

    const addToCart = () => {
        if (!selectedPkg) { toast.error(t42("Pick a class first.")); return; }
        if (requiredSlots > 0 && slotIds.length !== requiredSlots) {
            toast.error(requiredSlots === 1 ? 'Pick a class time first.' : `Pick ${requiredSlots} days first.`);
            return;
        }
        if (isSingleLessonNoSlots && !singleLessonAt) {
            toast.error(t42("Pick a date & time for the lesson."));
            return;
        }
        const picked = swimmers.filter((s) => selectedSwimmerUids.includes(s.uid));
        if (!picked.length) { toast.error(t42("Add at least one swimmer.")); return; }
        const pickedSlots = pkgSlots.filter((s) => slotIds.includes(s.id));
        const labels = pickedSlots.map(slotChipLabel).filter(Boolean).join(' + ');
        const newItems = [];
        for (const sw of picked) {
            const dupe = items.find((it) => it.package_id === selectedPkg.id
                && it.swimmer_uid === sw.uid
                && JSON.stringify(it.class_slot_ids || []) === JSON.stringify(slotIds));
            if (dupe) {
                toast.info(`${sw.first_name} is already in your cart for ${selectedPkg.name}.`);
                continue;
            }
            newItems.push({
                uid: newUid(),
                package_id: selectedPkg.id,
                package_name: selectedPkg.name,
                billing_type: selectedPkg.billing_type || 'one_time',
                price_cents: selectedPkg.price_cents || 0,
                billing_day_of_month: selectedPkg.billing_day_of_month || 1,
                class_slot_ids: [...slotIds],
                slot_labels: labels,
                single_lesson_at: singleLessonAt || null,
                swimmer_uid: sw.uid,
                swimmer_first_name: sw.first_name,
                swimmer_dob: sw.dob,
            });
        }
        if (newItems.length) {
            setItems((prev) => [...prev, ...newItems]);
            toast.success(`Added to your cart — ${newItems.length} item${newItems.length > 1 ? 's' : ''}.`);
        }
        // Reset schedule selection but keep age + program so "add another" is fast.
        setPackageId(null);
        setSlotIds([]);
        setSingleLessonAt('');
        setSelectedSwimmerUids([]);
    };

    const removeItem = (uid) => setItems((prev) => prev.filter((i) => i.uid !== uid));

    const totals = useMemo(() => {
        let oneTime = 0; let prorated = 0; let monthly = 0; let nextDate = null;
        for (const it of items) {
            if (it.billing_type === 'one_time') oneTime += it.price_cents || 0;
            else if (it.billing_type === 'recurring_monthly') {
                const est = estimateProration(it.price_cents || 0, it.billing_day_of_month);
                prorated += est.cents;
                monthly += it.price_cents || 0;
                if (!nextDate || est.anchorIso < nextDate) nextDate = est.anchorIso;
            }
        }
        // Every item in one cart belongs to the same school, so its currency is the cart currency.
        const currency = items.find((i) => i.currency)?.currency || currentSchoolCurrency();
        return { oneTime, prorated, monthly, dueToday: oneTime + prorated, nextDate, currency };
    }, [items]);

    const checkout = () => {
        if (!items.length) { toast.error(t42("Your cart is empty — add a class first.")); return; }
        if (previewMode) {
            toast.info(t42("Preview mode — checkout is disabled here. This is exactly where parents pay."));
            return;
        }
        storeCart(slug, { items, swimmers });
        navigate(`/coach/${slug}/enroll-cart`);
    };

    // ---- Render helpers ---------------------------------------------------
    const stepDone = {
        age: !!ageKey,
        program: !!programId,
        schedule: !!scheduleReady && selectedSwimmerUids.length > 0,
    };

    const stepper = [
        { n: 1, label: "Swimmer's age", done: stepDone.age, active: !ageKey },
        { n: 2, label: 'Program', done: stepDone.program, active: !!ageKey && !programId },
        { n: 3, label: 'Dates & time', done: !!scheduleReady, active: !!programId && !scheduleReady },
        { n: 4, label: "Who's swimming", done: selectedSwimmerUids.length > 0 && !!scheduleReady, active: !!scheduleReady },
        { n: 5, label: 'One checkout', done: false, active: items.length > 0 },
    ];

    return (
        <section className={embedded ? 'py-1' : 'bg-[#f4f7fb] py-12 lg:py-16'} data-testid="classfinder-wizard">
            <div className={embedded ? '' : 'mx-auto max-w-6xl px-4 sm:px-6'}>
                <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-[#00d4ff]" />
                    <h2 className="font-display text-2xl font-bold text-[#0a1628] sm:text-3xl">{t42("Find the perfect class")}</h2>
                </div>
                <p className="mt-1 text-[#1a3a5c]/80">{t42("Answer one question and we'll only show classes that fit your child.")}</p>

                {/* Stepper */}
                <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="classfinder-stepper">
                    {stepper.map((s, i) => (
                        <React.Fragment key={s.n}>
                            {i > 0 && <span className="text-[#c4d0de]">›</span>}
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${s.done || s.active ? 'text-[#0a1628]' : 'text-[#7a8aa0]'}`}>
                                <span className={`grid h-6 w-6 place-items-center rounded-full text-[11px] font-bold ${s.done || s.active ? 'bg-[#00d4ff] text-[#0a1628]' : 'bg-[#dde6f0] text-[#1a3a5c]'}`}>
                                    {s.done ? <Check className="h-3.5 w-3.5" /> : s.n}
                                </span>
                                {s.label}
                            </span>
                        </React.Fragment>
                    ))}
                </div>

                <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_340px] lg:items-start">
                    <div className="space-y-5">
                        {/* Step 1 — Age */}
                        <Card className="sd-card p-5 sm:p-6" data-testid="classfinder-step-age">
                            <h3 className="font-display text-base font-bold text-[#0a1628]">{t42("Step 1 — How old is your swimmer?")}</h3>
                            <p className="mt-0.5 text-sm text-[#1a3a5c]/70">{t42("We'll hide everything that doesn't fit.")}</p>
                            <div className="mt-4 flex flex-wrap gap-2.5">
                                {AGE_RANGES.map((r) => (
                                    <button
                                        key={r.key}
                                        type="button"
                                        onClick={() => pickAge(r.key)}
                                        className={`sd-focus min-h-11 rounded-full border-2 px-5 py-2 text-sm font-semibold transition-colors ${ageKey === r.key ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#dde6f0] bg-white text-[#1a3a5c] hover:border-[#00d4ff]'}`}
                                        data-testid={`classfinder-age-chip-${r.key}`}
                                    >
                                        {r.label}
                                    </button>
                                ))}
                            </div>
                        </Card>

                        {/* Step 2 — Program */}
                        {ageKey && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="classfinder-step-program">
                                <h3 className="font-display text-base font-bold text-[#0a1628]"> {t42("Step 2 — Pick a program")}{' '}
                                    <span className="text-xs font-medium text-[#7a8aa0]">({fitCount} {t42("of")} {programs.length} {t42("fit")} {range?.label?.toLowerCase()})</span>
                                </h3>
                                <p className="mt-0.5 text-sm text-[#1a3a5c]/70">{t42("Set up by")} {coach?.display_name || 'your coach'} {t42("— each groups its sessions & times.")}</p>
                                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                    {programs.map((p) => {
                                        const fits = programFitsRange(p, range);
                                        const sel = programId === p.id;
                                        const pMin = p.age_min ?? 0;
                                        const pMax = p.age_max;
                                        const ages = (pMax === null || pMax === undefined)
                                            ? (pMin <= 0 ? 'All ages' : `Ages ${pMin}+`)
                                            : (pMin <= 0 ? `Under ${pMax + 1}` : `Ages ${pMin}–${pMax}`);
                                        return (
                                            <button
                                                key={p.id}
                                                type="button"
                                                disabled={!fits}
                                                onClick={() => pickProgram(p)}
                                                className={`sd-focus relative rounded-2xl border-2 p-4 text-left transition-colors ${sel ? 'border-[#00d4ff] shadow-[0_0_0_3px_rgba(0,212,255,0.15)]' : 'border-[#dde6f0] bg-white'} ${!fits ? 'opacity-40' : 'hover:border-[#00d4ff]/60'}`}
                                                data-testid={`classfinder-program-card-${p.id}`}
                                            >
                                                {fits && (
                                                    <span className="absolute right-2.5 top-2.5 rounded-full bg-[#e8fbf1] px-2 py-0.5 text-[10px] font-bold text-[#16a34a]"> {t42("Fits")} {range?.label} <Check className="-mt-0.5 inline h-3 w-3" />
                                                    </span>
                                                )}
                                                <div className="text-2xl" aria-hidden>{p.icon || '🏊'}</div>
                                                <div className="mt-1.5 font-display text-sm font-bold text-[#0a1628]">{p.name}</div>
                                                <div className="mt-0.5 text-xs text-[#7a8aa0]">{ages}</div>
                                                {p.description && <div className="mt-1 line-clamp-2 text-xs text-[#1a3a5c]/70">{p.description}</div>}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Card>
                        )}

                        {/* Step 3 — Class + schedule (3 variants) */}
                        {selectedProgram && (
                            <Card ref={stepScheduleRef} className="sd-card p-5 sm:p-6" data-testid="classfinder-step-schedule">
                                <h3 className="font-display text-base font-bold text-[#0a1628]">{t42("Step 3 —")} {selectedProgram.name} {t42("· pick your class")}</h3>
                                <p className="mt-0.5 text-sm text-[#1a3a5c]/70">{t42("Choose an option, then your dates & times.")}</p>
                                {/* Phase 107 — location filter (only when classes span 2+ places) */}
                                {showLocationFilter && (
                                    <div className="mt-3" data-testid="classfinder-location-filter">
                                        <div className="text-xs font-semibold text-[#1a3a5c]"><MapPin className="mr-1 inline h-3.5 w-3.5" /> {t42("Location")}</div>
                                        <div className="mt-1.5 flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => pickLocationFilter(null)}
                                                className={`sd-focus min-h-9 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${!locationFilter ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#dde6f0] bg-white text-[#1a3a5c] hover:border-[#00d4ff]'}`}
                                                data-testid="classfinder-location-chip-all"
                                            > {t42("All locations")} </button>
                                            {locationChoices.opts.map((loc) => (
                                                <button
                                                    key={loc.id}
                                                    type="button"
                                                    onClick={() => pickLocationFilter(loc.id)}
                                                    className={`sd-focus min-h-9 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${locationFilter === loc.id ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#dde6f0] bg-white text-[#1a3a5c] hover:border-[#00d4ff]'}`}
                                                    data-testid={`classfinder-location-chip-${loc.id}`}
                                                >
                                                    {loc.name}
                                                </button>
                                            ))}
                                            {locationChoices.hasMobile && (
                                                <button
                                                    type="button"
                                                    onClick={() => pickLocationFilter('mobile')}
                                                    className={`sd-focus min-h-9 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-colors ${locationFilter === 'mobile' ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#dde6f0] bg-white text-[#1a3a5c] hover:border-[#00d4ff]'}`}
                                                    data-testid="classfinder-location-chip-mobile"
                                                > {t42("At your home")} </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {programPackages.length === 0 ? (
                                    <div className="mt-4 rounded-xl border border-dashed border-[#dde6f0] bg-white p-5 text-sm text-[#7a8aa0]" data-testid="classfinder-no-classes"> {t42("No classes in this program right now — check back soon or contact the coach.")} </div>
                                ) : visibleProgramPackages.length === 0 ? (
                                    <div className="mt-4 rounded-xl border border-dashed border-[#dde6f0] bg-white p-5 text-sm text-[#7a8aa0]" data-testid="classfinder-no-classes-at-location"> {t42("No classes in this program at that location.")}{' '}
                                        <button type="button" onClick={() => pickLocationFilter(null)} className="font-semibold text-[#0b7fa8] underline underline-offset-2" data-testid="classfinder-location-clear"> {t42("Show all locations")} </button>
                                    </div>
                                ) : (
                                    <div className="mt-4 space-y-3">
                                        {visibleProgramPackages.map((p) => {
                                            const sel = packageId === p.id;
                                            const isMonthly = p.billing_type === 'recurring_monthly';
                                            const isPerLesson = p.billing_type === 'per_lesson';
                                            const slots = p.class_slots || [];
                                            const dateRange = (p.start_date && p.end_date) ? formatDateRange(p.start_date, p.end_date) : null;
                                            return (
                                                <div
                                                    key={p.id}
                                                    className={`rounded-2xl border-2 transition-colors ${sel ? 'border-[#00d4ff] shadow-[0_0_0_3px_rgba(0,212,255,0.12)]' : 'border-[#dde6f0] bg-white'}`}
                                                    data-testid={`classfinder-class-card-${p.id}`}
                                                >
                                                    <button
                                                        type="button"
                                                        onClick={() => pickPackage(p)}
                                                        className="sd-focus flex w-full items-start justify-between gap-3 rounded-2xl p-4 text-left"
                                                        data-testid={`classfinder-class-select-${p.id}`}
                                                    >
                                                        <div className="min-w-0">
                                                            <div className="flex flex-wrap items-center gap-2">
                                                                <span className="font-display text-sm font-bold text-[#0a1628]">{p.name}</span>
                                                                {isMonthly && (
                                                                    <Badge className="bg-[#e6f9fd] text-[#0b7fa8] hover:bg-[#e6f9fd]"><Repeat className="mr-1 h-3 w-3" /> {t42("Monthly membership")}</Badge>
                                                                )}
                                                                {isPerLesson && (
                                                                    <Badge variant="outline" className="border-[#1a3a5c]/20 text-[#1a3a5c]">{t42("Pay per lesson")}</Badge>
                                                                )}
                                                                {/* Phase 153 — camps are badged so a parent browsing a program
                                                                    sees "oh, there's a camp for that" at a glance. */}
                                                                {p.package_kind === 'camp' && (
                                                                    <Badge className="bg-[#00d4ff]/15 text-[#0a1628] hover:bg-[#00d4ff]/15" data-testid={`classfinder-camp-badge-${p.id}`}><Tent className="mr-1 h-3 w-3" /> {t42("Camp")}</Badge>
                                                                )}
                                                                {p.is_popular && <Badge className="bg-[#ffd700] text-[#0a1628] hover:bg-[#ffd700]">{t42("Popular")}</Badge>}
                                                            </div>
                                                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#1a3a5c]/75">
                                                                {locationLabelFor(p) && (
                                                                    <span className="inline-flex items-center gap-1" data-testid={`classfinder-class-location-${p.id}`}>
                                                                        <MapPin className="h-3.5 w-3.5" /> {locationLabelFor(p)}
                                                                    </span>
                                                                )}
                                                                {dateRange && <span className="inline-flex items-center gap-1"><CalendarRange className="h-3.5 w-3.5" /> {dateRange}</span>}
                                                                {p.duration_minutes > 0 && <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {p.duration_minutes} {t42("min")}</span>}
                                                                {!isMonthly && !isPerLesson && Number(p.session_count) > 0 && <span>{p.session_count} {t42("lessons")}</span>}
                                                                {isMonthly && Number(p.sessions_per_week) > 0 && <span>{p.sessions_per_week}{t42("x / week")}</span>}
                                                                {p.flexible_schedule && <span>{t42("Flexible — coach schedules with you")}</span>}
                                                            </div>
                                                            {/* Phase 116a — teaching coach (name + photo) before enrolling */}
                                                            {p.teaching_coach && (
                                                                <div className="mt-2">
                                                                    <CoachChip coach={p.teaching_coach} size="xs" label={t42("Coach")} testId={`classfinder-class-coach-${p.id}`} />
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="shrink-0 text-right">
                                                            <div className="font-display text-lg font-bold text-[#0a1628]">{fmtPrice(p.price_cents, p?.currency)}</div>
                                                            <div className="text-[11px] text-[#7a8aa0]">{isMonthly ? t42("/ month") : isPerLesson ? t42("/ lesson") : t42("one-time")}</div>
                                                        </div>
                                                    </button>

                                                    {sel && (
                                                        <div className="border-t border-[#eef3f9] px-4 pb-4 pt-3">
                                                            {slots.length > 0 && (
                                                                <>
                                                                    <div className="text-xs font-semibold text-[#1a3a5c]">
                                                                        {requiredSlots > 1 ? `Pick your ${requiredSlots} days:` : t42("Pick your time:")}
                                                                    </div>
                                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                                        {slots.map((s) => {
                                                                            const cap = slotCapacity[s.id];
                                                                            const seatsLeft = cap && cap.capacity > 0 ? Math.max(0, cap.capacity - cap.taken) : null;
                                                                            const full = seatsLeft !== null && seatsLeft <= 0;
                                                                            const on = slotIds.includes(s.id);
                                                                            // Phase 164 — coach opted this class into the waitlist?
                                                                            const waitlistable = full && !!p.waitlist_enabled;
                                                                            const onList = !!waitlist[s.id];
                                                                            // Phase 171 — this time overlaps a lesson already on the
                                                                            // coach's calendar (an Open Schedule one-off). It can't be
                                                                            // bought instantly; the coach confirms a request first.
                                                                            const needsRequest = !full && !!cap?.conflict_requires_request;
                                                                            if (needsRequest) {
                                                                                return (
                                                                                    <button
                                                                                        key={s.id}
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            if (previewMode) { toast.info(t42("Preview only — approved parents request this time here.")); return; }
                                                                                            if (onRequestTime) { onRequestTime(p, s); return; }
                                                                                            toast.info(t42("Sign in to your parent account to request this time — your coach will confirm it with you."));
                                                                                        }}
                                                                                        className="sd-focus min-h-11 rounded-xl border-2 border-dashed border-[#ff6b35]/50 bg-[#fff5f0] px-3.5 py-2 text-left text-sm font-semibold text-[#0a1628] transition-colors hover:border-[#ff6b35]"
                                                                                        data-testid={`classfinder-slot-chip-${s.id}`}
                                                                                        data-needs-request="true"
                                                                                    >
                                                                                        {s.label ? `${s.label} · ` : ''}{slotChipLabel(s)}
                                                                                        <span className="flex items-center gap-1 text-[10px] font-semibold text-[#c65d15]">
                                                                                            <Clock className="h-3 w-3" /> {t42("Coach must confirm · Request this time")} </span>
                                                                                    </button>
                                                                                );
                                                                            }
                                                                            if (waitlistable) {
                                                                                return (
                                                                                    <button
                                                                                        key={s.id}
                                                                                        type="button"
                                                                                        disabled={wlBusy === s.id}
                                                                                        onClick={() => toggleWaitlist(p, s)}
                                                                                        aria-pressed={onList}
                                                                                        className={`sd-focus min-h-11 rounded-xl border-2 px-3.5 py-2 text-left text-sm font-semibold transition-colors ${onList ? 'border-[#ffd700] bg-[#fff8d6] text-[#0a1628]' : 'border-dashed border-[#dde6f0] bg-white text-[#1a3a5c]/70 hover:border-[#ffd700]'}`}
                                                                                        data-testid={`classfinder-slot-chip-${s.id}`}
                                                                                        data-on-waitlist={onList ? 'true' : 'false'}
                                                                                    >
                                                                                        <span className={onList ? '' : 'line-through'}>{s.label ? `${s.label} · ` : ''}{slotChipLabel(s)}</span>
                                                                                        <span className={`flex items-center gap-1 text-[10px] font-semibold ${onList ? 'text-[#7a5b00]' : 'text-[#c74300]'}`}>
                                                                                            <BellRing className="h-3 w-3" /> {onList ? `On waitlist${waitlist[s.id].position ? ` · #${waitlist[s.id].position}` : ''} · tap to leave` : t42("Full · Join waitlist")}
                                                                                        </span>
                                                                                    </button>
                                                                                );
                                                                            }
                                                                            return (
                                                                                <button
                                                                                    key={s.id}
                                                                                    type="button"
                                                                                    disabled={full}
                                                                                    onClick={() => toggleSlot(s)}
                                                                                    className={`sd-focus min-h-11 rounded-xl border-2 px-3.5 py-2 text-left text-sm font-semibold transition-colors ${on ? 'border-[#0a1628] bg-[#0a1628] text-white' : 'border-[#dde6f0] bg-white text-[#1a3a5c] hover:border-[#00d4ff]'} ${full ? 'opacity-40' : ''}`}
                                                                                    data-testid={`classfinder-slot-chip-${s.id}`}
                                                                                >
                                                                                    {s.label ? `${s.label} · ` : ''}{slotChipLabel(s)}
                                                                                    <span className={`block text-[10px] font-medium ${on ? 'text-[#9fd9ff]' : 'text-[#7a8aa0]'}`}>
                                                                                        {full ? t42("Full") : seatsLeft !== null ? `${seatsLeft} spot${seatsLeft === 1 ? '' : 's'} left` : formatSlotSchedule(s, selectedPkg) ? '' : ''}
                                                                                    </span>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </>
                                                            )}
                                                            {isSingleLessonNoSlots && (
                                                                <div className="mt-3">
                                                                    <Label htmlFor="classfinder-single-lesson-at" className="text-xs font-semibold text-[#1a3a5c]">{t42("Pick your lesson date & time")}</Label>
                                                                    <Input
                                                                        id="classfinder-single-lesson-at"
                                                                        type="datetime-local"
                                                                        value={singleLessonAt}
                                                                        onChange={(e) => setSingleLessonAt(e.target.value)}
                                                                        className="mt-1 h-11 max-w-xs rounded-xl"
                                                                        data-testid="classfinder-single-lesson-input"
                                                                    />
                                                                </div>
                                                            )}
                                                            {selectedPkg?.flexible_schedule && !slots.length && (
                                                                <div className="rounded-xl bg-[#eef3f9] p-3 text-xs text-[#1a3a5c]" data-testid="classfinder-flexible-note"> {t42("Flexible schedule —")} {coach?.display_name || 'the coach'} {t42("will set lesson times with you after checkout.")} </div>
                                                            )}
                                                            {isMonthly && (
                                                                <div className="mt-3 rounded-xl bg-[#e6f9fd] p-3 text-xs text-[#0a1628]" data-testid="classfinder-recurring-disclosure">
                                                                    <Repeat className="mr-1 inline h-3.5 w-3.5 text-[#0b7fa8]" />
                                                                    <strong>{t42("Recurring:")}</strong> {fmtPrice(selectedPkg.price_cents, selectedPkg?.currency)}{t42("/month billed on day")} {selectedPkg.billing_day_of_month || 1} {t42("of each month until you cancel. Your first month is")} <strong>{t42("prorated")}</strong> {t42("— today you only pay for the days left before your first billing day.")} </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </Card>
                        )}

                        {/* Step 4 — Who's swimming */}
                        {selectedPkg && scheduleReady !== undefined && (
                            <Card className="sd-card p-5 sm:p-6" data-testid="classfinder-step-swimmers">
                                <h3 className="font-display text-base font-bold text-[#0a1628]">{t42("Step 4 — Who's swimming?")}</h3>
                                <p className="mt-0.5 text-sm text-[#1a3a5c]/70">{t42("Pick one or more kids — siblings welcome.")}</p>
                                <div className="mt-4 flex flex-wrap items-center gap-2.5">
                                    {swimmers.map((s) => {
                                        const on = selectedSwimmerUids.includes(s.uid);
                                        const age = ageFromDob(s.dob);
                                        return (
                                            <button
                                                key={s.uid}
                                                type="button"
                                                onClick={() => toggleSwimmer(s.uid)}
                                                className={`sd-focus inline-flex min-h-11 items-center gap-2 rounded-xl border-2 px-3.5 py-2 text-sm font-semibold transition-colors ${on ? 'border-[#00d4ff] bg-[#f0fcff] text-[#0a1628]' : 'border-[#dde6f0] bg-white text-[#1a3a5c]'}`}
                                                data-testid={`classfinder-swimmer-chip-${s.uid}`}
                                            >
                                                <span className="grid h-6 w-6 place-items-center rounded-full bg-[#00d4ff] text-[11px] font-extrabold text-[#0a1628]">
                                                    {(s.first_name || '?').slice(0, 1).toUpperCase()}
                                                </span>
                                                {s.first_name}{age !== null ? ` (${age})` : ''}
                                                {on && <Check className="h-4 w-4 text-[#16a34a]" />}
                                            </button>
                                        );
                                    })}
                                    {!addingSwimmer && (
                                        <button
                                            type="button"
                                            onClick={() => setAddingSwimmer(true)}
                                            className="sd-focus inline-flex min-h-11 items-center gap-1.5 rounded-xl border-2 border-dashed border-[#c4d0de] px-3.5 py-2 text-sm font-semibold text-[#7a8aa0] hover:border-[#00d4ff] hover:text-[#0a1628]"
                                            data-testid="classfinder-add-swimmer-button"
                                        >
                                            <Plus className="h-4 w-4" /> {t42("Add swimmer")} </button>
                                    )}
                                </div>
                                {addingSwimmer && (
                                    <div className="mt-4 grid gap-3 rounded-2xl border border-[#dde6f0] bg-white p-4 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
                                        <div>
                                            <Label htmlFor="classfinder-swimmer-name" className="text-xs font-semibold text-[#1a3a5c]">{t42("First name")}</Label>
                                            <Input
                                                id="classfinder-swimmer-name"
                                                value={newSwimmer.first_name}
                                                onChange={(e) => setNewSwimmer((v) => ({ ...v, first_name: e.target.value }))}
                                                placeholder={t42("e.g. Emmie")}
                                                className="mt-1 h-11 rounded-xl"
                                                data-testid="classfinder-swimmer-name-input"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-xs font-semibold text-[#1a3a5c]">{t42("Birthdate")}</Label>
                                            <div className="mt-1">
                                                <DateOfBirthInput
                                                    value={newSwimmer.dob}
                                                    onChange={(iso) => setNewSwimmer((v) => ({ ...v, dob: iso }))}
                                                    data-testid="classfinder-swimmer-dob-input"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button type="button" onClick={addSwimmer} className="sd-cta h-11 rounded-xl px-4" data-testid="classfinder-swimmer-save-button"> {t42("Add")} </Button>
                                            <Button type="button" variant="outline" onClick={() => setAddingSwimmer(false)} className="h-11 rounded-xl" data-testid="classfinder-swimmer-cancel-button"> {t42("Cancel")} </Button>
                                        </div>
                                    </div>
                                )}

                                <Button
                                    type="button"
                                    onClick={addToCart}
                                    disabled={!scheduleReady || selectedSwimmerUids.length === 0}
                                    className="sd-focus mt-5 h-12 rounded-xl px-6 text-sm font-bold"
                                    style={{ background: accent, color: '#0a1628' }}
                                    data-testid="classfinder-add-to-cart-button"
                                >
                                    <ShoppingCart className="mr-1.5 h-4 w-4" /> {t42("Add to cart")} </Button>
                            </Card>
                        )}
                    </div>

                    {/* Sticky cart */}
                    <div className="lg:sticky lg:top-20" data-testid="classfinder-cart">
                        <div className="rounded-2xl bg-[#0a1628] p-5 text-white shadow-[0_18px_45px_rgba(10,22,40,0.25)]">
                            <div className="flex items-center justify-between">
                                <h3 className="font-display text-sm font-bold">{t42("Your classes")}</h3>
                                <span className="rounded-full bg-[#00d4ff] px-2.5 py-0.5 text-[11px] font-extrabold text-[#0a1628]" data-testid="classfinder-cart-count">{items.length}</span>
                            </div>
                            {items.length === 0 ? (
                                <p className="mt-3 rounded-xl border border-dashed border-white/25 p-4 text-xs text-[#9fb4cc]" data-testid="classfinder-cart-empty"> {t42("Nothing here yet — pick an age above to get started.")} </p>
                            ) : (
                                <div className="mt-3 space-y-2.5">
                                    {items.map((it) => (
                                        <div key={it.uid} className="rounded-xl bg-white/[0.07] p-3" data-testid={`classfinder-cart-item-${it.uid}`}>
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <div className="truncate text-[13px] font-bold">{it.package_name} — {it.swimmer_first_name}</div>
                                                    <div className="mt-0.5 text-[11px] text-[#9fb4cc]">
                                                        {it.slot_labels || (it.single_lesson_at ? it.single_lesson_at.replace('T', ' · ') : 'Flexible schedule')}
                                                        {it.billing_type === 'recurring_monthly' && ' · ongoing'}
                                                    </div>
                                                </div>
                                                <div className="shrink-0 text-right">
                                                    <div className="text-[13px] font-bold text-[#00d4ff]">
                                                        {fmtPrice(it.price_cents, it?.currency)}{it.billing_type === 'recurring_monthly' ? t42("/mo") : ''}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeItem(it.uid)}
                                                        className="sd-focus mt-1 inline-flex items-center gap-1 text-[10px] text-[#9fb4cc] hover:text-white"
                                                        data-testid={`classfinder-cart-remove-${it.uid}`}
                                                    >
                                                        <Trash2 className="h-3 w-3" /> {t42("Remove")} </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4 space-y-1 border-t border-white/15 pt-3 text-sm">
                                <div className="flex justify-between font-extrabold" data-testid="classfinder-cart-due-today">
                                    <span>{t42("Due today")}</span><span>{fmtPrice(totals.dueToday, totals.currency)}</span>
                                </div>
                                {totals.prorated > 0 && (
                                    <div className="flex justify-between text-[11px] text-[#9fb4cc]">
                                        <span>{t42("Includes prorated first month")}</span><span>{fmtPrice(totals.prorated, totals.currency)}</span>
                                    </div>
                                )}
                                {totals.monthly > 0 && (
                                    <div className="flex justify-between text-[11px] text-[#9fb4cc]" data-testid="classfinder-cart-monthly">
                                        <span>{t42("Then monthly")}{totals.nextDate ? ` (from ${totals.nextDate})` : ''}</span><span>{fmtPrice(totals.monthly, totals.currency)}{t42("/mo")}</span>
                                    </div>
                                )}
                            </div>

                            <Button
                                type="button"
                                onClick={checkout}
                                disabled={!items.length}
                                className="sd-focus mt-4 h-12 w-full rounded-full bg-[#ffd700] text-sm font-extrabold text-[#0a1628] hover:bg-[#ffd700]/90"
                                data-testid="classfinder-checkout-button"
                            > {t42("Checkout once")}{totals.dueToday > 0 ? ` — pay ${fmtPrice(totals.dueToday, totals.currency)}` : ''} <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                            <p className="mt-2.5 text-center text-[11px] text-[#9fb4cc]">{t42("One card entry · every swimmer enrolled")}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ClassFinderWizard;
